/**
 * OAuth Callback — Prompt 38 Part 1.5.
 *
 * The backend Google callback (302) lands the browser here with either:
 *   ?token=...&newUser=true|false&redirect=/&trialEndsAt=...
 * or
 *   ?error=<reason>
 *
 * We persist the token via the existing api.js helpers (same shape as
 * email/password login), tell the AuthProvider to refresh, then route the
 * user to their intended destination.
 */

import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { setAuthToken } from '../services/api';
import { useAuth } from '../hooks/useAuth';

const ERROR_MESSAGES = {
  oauth_disabled: 'Google sign-in is not configured on this deployment.',
  oauth_failed: 'We couldn\'t complete the Google sign-in. Please try again.',
  invalid_state: 'The sign-in link expired. Please start over.',
  missing_code: 'Google did not return a sign-in code.',
  no_id_token: 'Google did not return an ID token.',
  invalid_id_token: 'The Google ID token could not be verified.',
  email_not_verified: 'Your Google account email is not verified.',
  access_denied: 'You declined to share your Google account info.',
};

export default function AuthCallback() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [status, setStatus] = useState('processing'); // 'processing' | 'success' | 'error'
  const [errorMessage, setErrorMessage] = useState(null);
  const ranRef = useRef(false);

  useEffect(() => {
    // One-shot effect. The `ranRef` guard prevents double-execution.
    // We DO NOT return a cleanup function that cancels the navigate-timeout —
    // that was the original bug: when refreshUser() resolved and updated the
    // AuthProvider, this effect re-ran (because refreshUser is in the deps);
    // the cleanup cancelled the timer; the body early-returned; navigate
    // never fired. Result: user stuck on "Welcome to haythix — Redirecting…"
    if (ranRef.current) return;
    ranRef.current = true;

    const token = params.get('token');
    const error = params.get('error');
    const isNewUser = params.get('newUser') === 'true';
    const trialEndsAt = params.get('trialEndsAt');
    const redirect = params.get('redirect') || '/';

    if (error) {
      setErrorMessage(ERROR_MESSAGES[error] || 'Sign-in failed. Please try again.');
      setStatus('error');
      window.setTimeout(() => navigate('/login?error=oauth_failed', { replace: true }), 2200);
      return;
    }

    if (!token) {
      setErrorMessage('No sign-in token returned. Please try again.');
      setStatus('error');
      window.setTimeout(() => navigate('/login', { replace: true }), 1800);
      return;
    }

    // Persist token via existing helper (matches email/password login flow)
    setAuthToken(token);
    if (trialEndsAt) localStorage.setItem('trialEndsAt', trialEndsAt);

    // Refresh AuthProvider state so the rest of the app picks up the session.
    // Fire-and-forget — we don't await it so the navigate isn't blocked on
    // a slow /auth/me request.
    if (typeof refreshUser === 'function') {
      refreshUser().catch(() => {});
    }

    setStatus('success');
    const safeRedirect = redirect.startsWith('/') ? redirect : '/';
    const dest = isNewUser ? '/' : safeRedirect;
    // Schedule navigation. Once scheduled, let it fire — no cleanup that
    // could cancel it on effect re-run.
    window.setTimeout(() => navigate(dest, { replace: true }), 700);
  }, [params, navigate, refreshUser]);

  return (
    <div className="min-h-screen bg-dark-900 flex items-center justify-center p-4">
      <div className="card border-dark-500 max-w-md w-full text-center py-8">
        {status === 'processing' && (
          <>
            <Loader2 className="w-8 h-8 text-accent-primary animate-spin mx-auto mb-3" />
            <h1 className="text-white font-semibold">Signing you in…</h1>
            <p className="text-gray-400 text-sm mt-1">Verifying your Google account</p>
          </>
        )}
        {status === 'success' && (
          <>
            <CheckCircle className="w-8 h-8 text-emerald-400 mx-auto mb-3" />
            <h1 className="text-white font-semibold">Welcome to haythix</h1>
            <p className="text-gray-400 text-sm mt-1">Redirecting you to the dashboard…</p>
          </>
        )}
        {status === 'error' && (
          <>
            <AlertCircle className="w-8 h-8 text-red-400 mx-auto mb-3" />
            <h1 className="text-white font-semibold">Sign-in failed</h1>
            <p className="text-gray-400 text-sm mt-2">{errorMessage}</p>
            <Link to="/login" className="inline-block mt-4 text-sm text-accent-primary hover:underline">
              Back to login
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
